import React, { useContext } from 'react';
import { RadioGroupField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './RPSTRACDistribution.css';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import AlertMessage from '../AlertMessage/AlertMessage';
import { getCustomersEndOfYearAge } from './utils';

const DisplayCashDistributionQuestion = ({
  participantLoanBalanceText,
  handleParticipantCashDistribution,
  participantCashDistribution,
  participantCashDistributionText
}) => {
  const radioOptions = [
    {
      title: 'Yes',
      value: 'YES'
    },
    {
      title: 'No',
      value: 'NO'
    }
  ];

  const { formProps } = useContext(FlowContext);

  return (
    <>
      <div className="additional-text" data-name="participantLoanBalanceText">
        {participantLoanBalanceText}
      </div>
      <div className="distribution-option-input">
        <RadioGroupField
          title="Does the participant want a cash distribution?"
          options={radioOptions}
          onChange={handleParticipantCashDistribution}
          {...formProps.participantCashDistribution}
          name="participantCashDistribution"
        />
      </div>
      {participantCashDistribution === 'YES' && (
        <div
          className="additional-text"
          data-name="participantCashDistributionText">
          {participantCashDistributionText}
        </div>
      )}
      <style jsx>{styles}</style>
    </>
  );
};

const DisplayAdditionalQuestions = ({
  handleParticipantStatus,
  participantStatus,
  completeDistributionText,
  handleParticipantAge,
  participantAge,
  participantAgeText,
  handleBrokerageAssets,
  brokerageAssets,
  brokerageAssetsText,
  handleParticipantLoanBalance,
  participantLoanBalance,
  participantLoanBalanceText,
  handleParticipantCashDistribution,
  participantCashDistribution,
  participantCashDistributionText
}) => {
  const radioOptions = [
    {
      title: 'Yes',
      value: 'YES'
    },
    {
      title: 'No',
      value: 'NO'
    }
  ];

  const { formProps, appState } = useContext(FlowContext);

  const {
    store: {
      app: {
        customer: { dob }
      }
    }
  } = appState;

  return (
    <>
      <div className="distribution-option-input">
        <RadioGroupField
          title='Is the participant status "Terminated" or "Retired"?'
          options={radioOptions}
          onChange={handleParticipantStatus}
          {...formProps.participantStatus}
          name="participantStatus"
        />
      </div>
      {participantStatus === 'NO' && (
        <div className="additional-text" data-name="completeDistributionText">
          {completeDistributionText}
        </div>
      )}
      <div className="distribution-option-input">
        <RadioGroupField
          title="Will the participant be 72 by the end of the year?"
          options={radioOptions}
          onChange={handleParticipantAge}
          {...formProps.participantAge}
          name="participantAge"
        />
      </div>
      {participantAge === 'YES' && (
        <>
          <div className="additional-text" data-name="participantAgeText">
            {participantAgeText}
          </div>
          <div className="additional-text" data-name="customerAgeText">
            {getCustomersEndOfYearAge(dob)}
          </div>
        </>
      )}
      <div className="distribution-option-input">
        <RadioGroupField
          title="Does the participant have brokerage assets?"
          options={radioOptions}
          vertical
          onChange={handleBrokerageAssets}
          {...formProps.brokerageAssets}
          name="brokerageAssets"
        />
      </div>
      {brokerageAssets === 'YES' && (
        <div className="additional-text" data-name="brokerageAssetsText">
          {brokerageAssetsText}
        </div>
      )}
      <div className="distribution-option-input">
        <RadioGroupField
          title="Does the participant have an outstanding loan balance?"
          options={radioOptions}
          onChange={handleParticipantLoanBalance}
          {...formProps.participantLoanBalance}
          name="participantLoanBalance"
        />
      </div>
      {participantLoanBalance === 'YES' && (
        <DisplayCashDistributionQuestion
          participantLoanBalanceText={participantLoanBalanceText}
          handleParticipantCashDistribution={handleParticipantCashDistribution}
          participantCashDistribution={participantCashDistribution}
          participantCashDistributionText={participantCashDistributionText}
        />
      )}
      <style jsx>{styles}</style>
    </>
  );
};

export const associateDisclaimerText = `Please answer the following questions to determine what steps the participant
will take to complete the distribution process.`;

export const completeDistributionText = `If applicable, open the Rollover IRA account by clicking Submit below.
The participant will need to complete the distribution at a later time.`;

export const participantAgeText = `Please open the Rollover IRA account by clicking Submit below. The participant 
will need to complete the distribution plan paperwork to process the RMD and finalize the rollover process.`;

export const brokerageAssetsText = `If applicable, please open the Rollover IRA account by clicking Submit below. 
Brokerage assets need to be liquidated before the distribution and rollover may be completed.`;

export const participantLoanBalanceText = `The balance of your outstanding loan will be processed as a taxable 
distribution at the time of your rollover.`;

export const participantCashDistributionText = `Since the participant has an outstanding loan and wants a cash 
distribution, there are additional tax consequences to be considered. Please inform the participant that 
they need to process the rollover by paperwork.`;

export const distributionStatusText = `Please open the Rollover IRA account by clicking the Submit button below. 
The participant will need to complete the distribution plan paperwork to finalize the rollover process.`;

const RPSTRACDistribution = ({
  setPaperlessWithdrawls,
  paperlessWithdrawls,
  participantStatus,
  setParticipantStatus,
  participantAge,
  setParticipantAge,
  brokerageAssets,
  setBrokerageAssets,
  participantLoanBalance,
  setParticipantLoanBalance,
  participantCashDistribution,
  setParticipantCashDistribution,
  distributionStatus,
  setDistributionStatus
}) => {
  const { formProps } = useContext(FlowContext);

  const handlePaperlessWithdrawls = ({ target: { value } }) => {
    if (value === 'YES') {
      setParticipantStatus('');
      setParticipantAge('');
      setBrokerageAssets('');
      setParticipantLoanBalance('');
      setParticipantCashDistribution('');
    }
    setPaperlessWithdrawls(value);
  };
  const handleParticipantStatus = ({ target: { value } }) => {
    setParticipantStatus(value);
  };
  const handleParticipantAge = ({ target: { value } }) => {
    setParticipantAge(value);
  };

  const handleBrokerageAssets = ({ target: { value } }) => {
    setBrokerageAssets(value);
  };
  const handleParticipantLoanBalance = ({ target: { value } }) => {
    if (value === 'NO') {
      setParticipantCashDistribution('');
    }
    setParticipantLoanBalance(value);
  };
  const handleParticipantCashDistribution = ({ target: { value } }) => {
    setParticipantCashDistribution(value);
  };
  const handleDistributionStatus = ({ target: { value } }) => {
    setDistributionStatus(value);
  };

  const radioOptions = [
    {
      title: 'Yes',
      value: 'YES'
    },
    {
      title: 'No',
      value: 'NO'
    }
  ];

  return (
    <div>
      <div className="section-header">
        <SectionHeading text="Distribution Determination" />
        <AlertMessage message={associateDisclaimerText} />
        <h5 className="sub-title">Distribution Questions</h5>
      </div>
      <div className="section-content">
        <div className="distribution-option-input">
          <RadioGroupField
            title="Does the plan allow for pre-authorized paperless withdrawals?"
            options={radioOptions}
            onChange={handlePaperlessWithdrawls}
            {...formProps.paperlessWithdrawls}
            name="paperlessWithdrawls"
          />
        </div>
        {paperlessWithdrawls === 'NO' && (
          <div className="additional-text" data-name="completeDistributionText">
            {completeDistributionText}
          </div>
        )}

        {paperlessWithdrawls === 'NO' || (
          <DisplayAdditionalQuestions
            handleParticipantStatus={handleParticipantStatus}
            participantStatus={participantStatus}
            completeDistributionText={completeDistributionText}
            handleParticipantAge={handleParticipantAge}
            participantAge={participantAge}
            participantAgeText={participantAgeText}
            handleBrokerageAssets={handleBrokerageAssets}
            brokerageAssets={brokerageAssets}
            brokerageAssetsText={brokerageAssetsText}
            handleParticipantLoanBalance={handleParticipantLoanBalance}
            participantLoanBalance={participantLoanBalance}
            participantLoanBalanceText={participantLoanBalanceText}
            handleParticipantCashDistribution={
              handleParticipantCashDistribution
            }
            participantCashDistribution={participantCashDistribution}
            participantCashDistributionText={participantCashDistributionText}
          />
        )}
        <div className="distribution-option-input">
          <RadioGroupField
            title="Will the distribution be performed during this phone call with the participant?"
            options={radioOptions}
            onChange={handleDistributionStatus}
            {...formProps.distributionStatus}
            name="distributionStatus"
          />
        </div>
        {distributionStatus === 'NO' && (
          <div className="additional-text" data-name="distributionStatusText">
            {distributionStatusText}
          </div>
        )}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export default RPSTRACDistribution;
